import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Children, CSSProperties, ReactNode, RefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { Colours } from "../Themes/Colours";
import { v4 } from "uuid";
import { faCaretDown, faChevronDown, faChevronRight, faEllipsisH, faEllipsisV, faTools } from "@fortawesome/free-solid-svg-icons";

export const InterfaceStyles = {
    flexOne : {
        display:'flex',
        flex:1,
    } as CSSProperties,
    center : {
        display:'flex',
        justifyContent:'center',
        alignItems:'center',
    } as CSSProperties,
    iconContainer : {
        display:'flex',
        justifyContent:'center',
        alignItems:'center',
        height:40,
        width:40,
    } as CSSProperties,
    standardColumn:{
        display:'flex',
        flexDirection: 'column',
        flexShrink:0,
        gap:10,
        overflow:'hidden',
    } as CSSProperties,
    standardRow:{
        display:'flex',
        height:40,
        flexShrink:0,
        gap:10,
        alignItems:'center',
    } as CSSProperties,
    settingsRow:{
        display:'flex',
        height:40,
        flexShrink:0,
        paddingRight:10,
        gap:10,
        boxSizing:"border-box",
        alignItems:'center',
    } as CSSProperties,
    elementRow:{
        display:'flex',
        height:40,
        flexShrink:0,
        paddingLeft:10,
        paddingRight:10,
        gap:10,
        boxSizing:"border-box",
        alignItems:'center',
        overflow:'hidden',
    } as CSSProperties,
    labelExtension:{
        display:'flex',
        height:40,
        alignItems:'center',
        justifyContent:'center',
        backgroundColor:Colours.levelTwoBg,
        color:Colours.levelTwoText,
        boxSizing:'border-box',
    } as CSSProperties, 
    label:{
        display:'flex',
        height:40,
        alignItems:'center',
        padding:10,
        backgroundColor:Colours.levelTwoBg,
        color:Colours.levelTwoText,
        borderTopRightRadius:5,
        borderBottomRightRadius:5,
        boxSizing:'border-box',
    } as CSSProperties, 
    button:{
        display:'flex',
        flexDirection:'row',
        height:40,
        width:40,
        minWidth:40,
        gap:10,
        backgroundColor:Colours.levelOneBg,
        borderRadius:5,
        borderColor:Colours.levelThreeBg,
        borderStyle:'solid',
        borderWidth:1,
        alignItems:'center',
        justifyContent:'center',
        overflow:'hidden',
        boxSizing:"border-box",
        cursor:'pointer',
    } as CSSProperties,
    buttonToggleOn:{
        borderColor:Colours.orangeHighlightBg,
    } as CSSProperties,
    buttonText:{
        fontSize:16,
        color:Colours.levelTwoText,
        whiteSpace:'nowrap',
        overflow:'hidden'
    } as CSSProperties,
    buttonIconToggleOn:{
        color:Colours.orangeHighlightBg,
    } as CSSProperties,
    buttonIconToggleOff:{
        color:Colours.levelThreeBg,
    } as CSSProperties,
    floatingSection:{
        display:'flex',
        gap:10,
        flexDirection:'column',
        backgroundColor:Colours.levelOneBg,
        borderRadius:5,
        border: `1px solid ${Colours.levelThreeBg}`,
        overflow:'hidden',
        boxSizing:"border-box",
    } as CSSProperties,
    shadow:{
        boxShadow: "0px 4px 15px -7px rgba(0, 0, 0, 0.26)", 
    } as CSSProperties,
    input:{
        display:'flex',
        flex:1,
        height:40,
        backgroundColor:Colours.levelOneBg, 
        borderRadius:5, 
        color:Colours.levelTwoText,
        padding:0,
        paddingLeft:10,
        paddingRight:10, 
        border: `1px solid ${Colours.levelThreeBg}`,
        textAlign:'right',
        overflow:'hidden',
        outline:'none',
        boxSizing:"border-box",
        maxWidth:'100%',
        fontSize:16,
    } as CSSProperties,
};

export const InterfaceLabelItem = (props:{
    icon?:IconDefinition,
    label:string,
    flexLabel?:boolean,
    children?:ReactNode
    labelStyle?:CSSProperties,
    style?:CSSProperties,
}) => {
    return(
        <div 
            style={{...InterfaceStyles.settingsRow,...props.style}}
        >
            {props.icon && (
                <div style={{...InterfaceStyles.labelExtension,paddingLeft:8,marginRight:-10,...props.labelStyle}}>
                    <FontAwesomeIcon icon={props.icon} color={Colours.levelTwoText}></FontAwesomeIcon>
                </div>
            )}
            <div style={{...InterfaceStyles.label, flex:props.flexLabel ? 1 : undefined,...props.labelStyle}}>{props.label}</div>
            {props.children}
        </div>
    );
};

export const InterfaceSection = (props:{
    title:string, 
    flexTitle?:boolean, 
    titleWidth?:number,
    titleColour?:string,
    openIcon?:IconDefinition,
    closedIcon?:IconDefinition,
    titleIcon?:IconDefinition, 
    backgroundColour?:string, 
    titleControls?:ReactNode, 
    children?:ReactNode,
    containerStyle?:CSSProperties,
    startCollapsed?:boolean,
    flex?:boolean,
    alwaysOpen?:boolean,
    onTitleEdit?:(value:string)=>void    
}) => {

    const [collapsed, setCollapsed] = useState(props.startCollapsed ?? false);

    return(
        <div style={{...InterfaceStyles.standardColumn, flex: props.flex ? 1 : 0, paddingBottom:1,}}>
            <div style={{...InterfaceStyles.settingsRow,gap:0,paddingRight:0,alignItems:undefined}}>   
                <div style={{display:'flex',flexDirection:'row',flex:props.flexTitle ? 1 : undefined}}                    
                    onClick={()=>{
                        props.alwaysOpen !== true && setCollapsed(!collapsed);
                    }}
                >
                    <div style={{...InterfaceStyles.standardColumn,backgroundColor:collapsed ? Colours.levelTwoBg : props.backgroundColour ?? Colours.levelThreeBg, alignItems:'center',justifyContent:'center',paddingLeft:10, display: props.alwaysOpen ? 'none' : 'flex'}}>
                        <FontAwesomeIcon icon={collapsed ? props.closedIcon ?? props.openIcon ?? faChevronRight : props.openIcon ?? faChevronDown} color={collapsed ? Colours.levelZeroText : props.titleColour ?? Colours.levelTwoText}></FontAwesomeIcon>
                    </div>
                    {props.titleIcon && (
                        <div style={{...InterfaceStyles.standardColumn,backgroundColor:collapsed ? Colours.levelTwoBg : props.backgroundColour ?? Colours.levelThreeBg, alignItems:'center',justifyContent:'center',paddingLeft:10,marginLeft:-1}}>
                            <FontAwesomeIcon icon={props.titleIcon} color={collapsed ? Colours.levelZeroText : props.titleColour ?? Colours.levelTwoText}></FontAwesomeIcon>
                        </div>
                    )}
                    <InterfaceTextInput value={props.title} readOnly={props.onTitleEdit ?  false : true} onSubmit={(value)=>{
                        props.onTitleEdit?.(value);
                    }} style={{
                        ...InterfaceStyles.label, 
                        borderRadius:0,
                        backgroundColor:collapsed ? Colours.levelTwoBg : props.backgroundColour ?? Colours.levelThreeBg,
                        flex: props.flexTitle ? 1 : undefined,
                        width: props.flexTitle ? undefined : props.titleWidth ?? 110,
                        borderTopRightRadius:0,
                        color:collapsed ? Colours.levelZeroText : props.titleColour ?? Colours.levelTwoText,
                        border: collapsed ? `1px solid ${Colours.levelTwoBg}` : `1px solid ${Colours.levelThreeBg}`,
                        padding:0,
                        paddingLeft:10,
                        textAlign: props.onTitleEdit ? 'left' : undefined,
                    }}></InterfaceTextInput>
                </div>
                <div style={{
                    display:'flex',
                    flex: props.flexTitle ? undefined : 1,
                    backgroundColor:collapsed ? Colours.levelTwoBg : props.backgroundColour ?? Colours.levelThreeBg,
                    marginLeft:-1,
                }}>
                    <div style={{
                        display:'flex',
                        backgroundColor:Colours.levelOneBg,
                        flex: 1,
                        borderTopLeftRadius:5,
                        marginTop:2,
                        padding:10,
                        paddingTop:7,
                        paddingLeft: props.titleControls ? 10 : 0,
                        paddingBottom:0,
                        overflow:'hidden',
                    }}>{props.titleControls}</div>
                </div>
            </div>
            <div style={{...InterfaceStyles.standardColumn, flex: props.flex ? 1 : 0, display: collapsed ? 'none' : 'flex',overflow:'hidden',...props.containerStyle}}>
                {props.children}
            </div>
        </div>
    );

};

export const InterfaceScroller = (props:{direction?:'horizontal' | 'vertical', children?:ReactNode, containerStyle?:CSSProperties, scrollerStyle?:CSSProperties}) => {

    const [direction, setDirection] = useState(props.direction ?? 'vertical');
    const [isMobile, setIsMobile] = useState(false);

    useEffect(()=>{
        setDirection(props.direction ?? 'vertical');
    },[props.direction]);

    useEffect(()=>{
        const checkValue = window.navigator.userAgent.toLowerCase();
        checkValue.includes('iphone') && setIsMobile(true);
        checkValue.includes('android') && setIsMobile(true);
    },[]);

    return(
        <div style={{
            display:'flex',
            flex:1,
            flexDirection: direction === 'vertical' ? 'column' : 'row',
            boxSizing:"border-box",
            overflow:'hidden',
            ...props.containerStyle,
        }}>
            <div style={{
                display:'flex',
                flexDirection: direction === 'vertical' ? 'column' : 'row',
                overflowY: direction === 'vertical' ? 'scroll' : 'hidden',
                overflowX: direction === 'horizontal' ? 'scroll' : 'hidden',
                marginRight: direction === 'vertical' ? isMobile ? 0 : -15 : undefined,
                marginBottom: direction === 'horizontal' ? isMobile ? 0 : -15 : undefined,
                paddingBottom:10,
                paddingTop:10,
                gap:10,
                boxSizing:"border-box",
                ...props.scrollerStyle,
            }}>
                {props.children}
            </div>
        </div>
    );

};

export const InterfaceButton = (props:{
    icon?:IconDefinition,
    text?:string,
    state?:boolean,
    style?:CSSProperties,
    textStyle?:CSSProperties,
    children?:ReactNode,
    onClick?:React.MouseEventHandler<HTMLDivElement>,
}) => {

    const [hovering, setHovering] = useState(false);

    const adjustedButtonStyle:CSSProperties = {
        ...InterfaceStyles.button,
        paddingLeft: props.text ? 10 : 0,
        paddingRight: props.text ? 10 : 0,
        width: props.text ? undefined : InterfaceStyles.button.width,
    };

    return(
        <div
            style={{
                ...(props.state ? {...adjustedButtonStyle, ...InterfaceStyles.buttonToggleOn} : {...adjustedButtonStyle}),
                backgroundColor: hovering ? Colours.blueHighlightBg : Colours.levelOneBg,
                ...props.style,
            }}
            onContextMenu={(e)=>e.preventDefault()}
            onClick={props.onClick}
            onMouseEnter={()=>setHovering(true)}
            onMouseLeave={()=>setHovering(false)}
        >
            {props.icon && <FontAwesomeIcon icon={props.icon} style={{
                fontSize:16,
                ...(props.state !== undefined ? props.state ? InterfaceStyles.buttonIconToggleOn : InterfaceStyles.buttonIconToggleOff : InterfaceStyles.buttonText),
                ...(hovering ? {color:'white'} : {}),
            }}></FontAwesomeIcon>}
            {props.text && <div style={{
                fontSize:16,
                ...(props.state !== undefined ? props.state ? InterfaceStyles.buttonIconToggleOn : InterfaceStyles.buttonIconToggleOff : InterfaceStyles.buttonText),
                ...(hovering ? {color:'white'} : {}),
                ...props.textStyle,
            }}>{props.text}</div>}
            {props.children}
        </div>
    );

};

export const InterfaceTextInput = (props:{
    value:string,
    placeholder?:string,
    maxLength?:number,
    readOnly?:boolean,
    style?:CSSProperties,
    ref?:RefObject<HTMLInputElement | null>
    onSubmit?:(value:string)=>void,
}) => {

    //const Keyboard = useKeyboard();

    const [value, setValue] = useState('');

    useEffect(()=>{
        setValue(props.value);
    },[props.value]);

    return(
        <input
            ref={props.ref}
            disabled={props.readOnly ?? undefined}
            placeholder={props.placeholder ?? undefined}
            maxLength={props.maxLength ?? undefined}
            value={value}
            style={{
                ...InterfaceStyles.input,
                width:'100%',
                ...props.style,
            }}
            onChange={(e)=> setValue(e.target.value)}
            onKeyDown={(e)=>{
                if(e.key === 'Enter'){
                    props.onSubmit?.(value);
                }
            }}
            onFocus={()=>{
                //Keyboard.setBypass?.(true);
            }}
            onBlur={()=>{
                //Keyboard.setBypass?.(false);
            }}
        ></input>
    );

};

export const InterfaceNumericInput = (props:{
    value:number,
    minValue?:number,
    maxValue?:number,
    dragSpeed?:number,
    decimalLength?:number,
    label?:string, 
    labelWidth?:number, 
    suffix?:string,
    readonly?:boolean,
    style?:CSSProperties,
    onLabelClick?:()=>void,
    onSuffixClick?:()=>void,
    onSubmit?:(value:number)=>void
}) => {

    const [value, setValue] = useState<string>('');

    useEffect(()=>{
        let curVal = props.value;
        props.decimalLength !== undefined && (curVal = +curVal.toFixed(props.decimalLength));
        setValue(curVal.toString());
    },[props.value,props.decimalLength]);

    return(
        <div style={{
            ...InterfaceStyles.elementRow,
            flex:1,
            border: `1px solid ${Colours.levelThreeBg}`,
            backgroundColor: Colours.levelThreeBg,
            borderRadius:5,
            overflow:'hidden',
            alignItems:'center',
            paddingLeft: props.label ? 10 : 0,
            paddingRight: props.suffix ? 10 : 0,
            ...props.style,
        }}>
            {props.label && (
                <div 
                    style={{
                        ...InterfaceStyles.buttonText,
                        width: props.labelWidth ?? 'undefined',
                        textAlign:'center',
                        userSelect:'none',
                        cursor: props.onLabelClick !== undefined ? 'pointer' : undefined
                    }}
                    onClick={props.onLabelClick}
                >
                    {props.label}
                </div>
            )}
            <InterfaceTextInput 
                value={value}
                readOnly={props.readonly}
                style={{
                    
                }}
                onSubmit={(valueString)=>{
                    let numericValue = +valueString;
                    if(!Number.isNaN(numericValue)){
                        props.maxValue && numericValue > props.maxValue && (numericValue = props.maxValue);
                        props.minValue && numericValue < props.minValue && (numericValue = props.minValue);
                        props.decimalLength && (numericValue = +numericValue.toFixed(props.decimalLength))
                        setValue(numericValue.toString());
                        props.onSubmit?.(numericValue);
                    }
                }}
            ></InterfaceTextInput>
            {props.suffix && (
                <div 
                    style={{
                        ...InterfaceStyles.buttonText,
                        userSelect:'none', 
                        cursor: props.onSuffixClick !== undefined ? 'pointer' : undefined
                    }}
                    onClick={props.onSuffixClick}
                >
                    {props.suffix}
                </div>
            )}
        </div>
    );
};

export const InterfacePartition = (props:{
    direction:'row' | 'column',
    initialSizes?:number[], 
    handleStyles?:CSSProperties,
    children?:ReactNode
}) => {

    const [sectionSizes, setSectionSizes] = useState<number[]>([]);

    const id = useRef(v4());

    const [resizing, setResizing] = useState(false);
    const [targetHandleIndex, setTargetHandleIndex] = useState(-1);
    const partitionRef = useRef<HTMLDivElement>(null);

    const changeSize = (sectionIndex:number,position:number) => {
        // get the space available to both and then set both!
        // calculate position of this section
        // find position inside of space
        const rect = partitionRef.current?.getBoundingClientRect();
        if(rect){
            const pixelSizes = {
                before:0,
                first:0,
                second:0,
                both:0,
                after:0,
                total:0,
            };
            sectionSizes.map((value,index) => {
                const key = props.direction === 'column' ? 'height' : 'width';
                const size = rect[key] * (value / 100);
                index < sectionIndex && (pixelSizes.before += size);
                if(index === sectionIndex){
                    pixelSizes.first = size;
                    pixelSizes.both += size;
                }
                if(index === sectionIndex + 1){
                    pixelSizes.second = size;
                    pixelSizes.both += size;
                }
                index > sectionIndex + 1 && (pixelSizes.after += size);
                pixelSizes.total += size;
            });
            const positionInPartition = position - (props.direction === 'column' ? rect.y : rect.x);
            const positionInSection = positionInPartition - pixelSizes.before;
            const percentageOfSection = positionInSection / pixelSizes.both;
            let newFirstPixelSize = (pixelSizes.both * percentageOfSection);
            newFirstPixelSize < 100 && (newFirstPixelSize = 100);
            let newSecondPixelSize = (pixelSizes.both - newFirstPixelSize);
            if(newSecondPixelSize < 100){
                newFirstPixelSize = pixelSizes.both - 100;
                newSecondPixelSize = 100;
            }
            const newFirstPercentage = (newFirstPixelSize / pixelSizes.total) * 100;
            const newSecondPercentage = (newSecondPixelSize / pixelSizes.total) * 100;
            const copy = [...sectionSizes];
            copy[sectionIndex] = newFirstPercentage;
            copy[sectionIndex + 1] = newSecondPercentage;
            setSectionSizes(copy);
        }
    };

    const [initialSet, setInitialSet] = useState(false);

    useEffect(()=>{
        if(!initialSet){
            const tempSizes:number[] = [];
            let length = Children.count(props.children);
            let remainingSpace = 100;
            Children.map(props.children,(child,index)=>{
                if(props.initialSizes && props.initialSizes[index]){
                    remainingSpace -= props.initialSizes[index];
                    length -= 1;
                }
                tempSizes.push(props.initialSizes ? props.initialSizes[index] ?? remainingSpace / length : remainingSpace / length);
            });
            setSectionSizes(tempSizes);
            setInitialSet(true);
        }
    },[props.initialSizes,props.children]);

    /* useEffect(()=>{
        console.log(sectionSizes)
    },[sectionSizes]) */

    return(
        <div 
            ref={partitionRef} 
            style={{
                display:'flex',
                flex:1,
                flexDirection:props.direction,
                overflow:'hidden',
            }}
            onMouseDown={()=>setResizing(true)}
            onMouseUp={()=>{
                setResizing(false)
                setTargetHandleIndex(-1);
            }}
            onMouseMove={(evt)=>{
                if(resizing && targetHandleIndex !== -1){
                    changeSize(targetHandleIndex,props.direction === 'column' ? evt.pageY : evt.pageX);
                }
            }}
        >
            {Children.map(props.children,((child,index) => (
                <>
                    <InterfacePartitionSection 
                        style={{
                            flexDirection:props.direction,
                            flex: sectionSizes[index] === undefined ? 1 : undefined,
                            height: (sectionSizes[index] && props.direction) === 'column' ? `${sectionSizes[index]}%` : undefined,
                            width: (sectionSizes[index] && props.direction) === 'row' ? `${sectionSizes[index]}%` : undefined,
                            maxHeight: (sectionSizes[index] && props.direction) === 'column' ? `${sectionSizes[index]}%` : undefined,
                            maxWidth: (sectionSizes[index] && props.direction) === 'row' ? `${sectionSizes[index]}%` : undefined,
                        }}
                    >{child}</InterfacePartitionSection>
                    {index !== Children.count(props.children) - 1 && (
                        <InterfacePartitionHandle 
                            key={`${id.current}-handle-${index}`} 
                            index={index} 
                            direction={props.direction} 
                            handleStyles={props.handleStyles}
                            targetHandleIndex={targetHandleIndex}
                            setTargetHandleIndex={setTargetHandleIndex}
                            resizing={resizing}
                        ></InterfacePartitionHandle>
                    )}
                </>
            )))}
        </div>
    );

};

const InterfacePartitionSection = (props:{style?:CSSProperties,children?:ReactNode}) => {
    return(
        <div style={{
            display:'flex',
            overflow:'hidden',
            borderRadius:5,
            ...props.style,
        }}>
            {props.children}
        </div>
    )
};

const InterfacePartitionHandle = (props:{
        index:number,
        direction:'row' | 'column',
        resizing:boolean,
        targetHandleIndex:number,
        setTargetHandleIndex:React.Dispatch<SetStateAction<number>>, 
        handleStyles?:CSSProperties,
    }) => {

        return(
            <div 
                style={{
                    display:'flex',
                    position:'relative',
                    height: props.direction === 'column' ? 15 : undefined,
                    width: props.direction === 'row' ? 15 : undefined,
                    backgroundColor: Colours.levelZeroBg,
                    ...InterfaceStyles.center,
                    ...props.handleStyles,
                }}
            >
                <FontAwesomeIcon 
                    icon={props.direction === 'column' ? faEllipsisH : faEllipsisV}
                    style={{
                        color: props.index === props.targetHandleIndex ? Colours.levelFourBg : Colours.levelTwoBg,
                        fontSize:32,
                    }}
                ></FontAwesomeIcon>
                <div 
                    style={{
                        position:'absolute',
                        width: props.direction === 'column' ? '100%' : 30,
                        height: props.direction === 'row' ? '100%' : 30,
                        zIndex:2,
                        cursor: props.direction === 'column' ? 'ns-resize' : 'ew-resize',
                    }}
                    onMouseEnter={()=>{
                        !props.resizing && props.setTargetHandleIndex(props.index);
                    }}
                    onMouseLeave={()=>{
                        !props.resizing && props.setTargetHandleIndex(-1);
                    }}
                ></div>
            </div>
        );
    };

export const InterfacePanelTitle = (props:{icon:IconDefinition,text:string,children?:ReactNode}) => {
    return(
        <div>
            <div style={{...InterfaceStyles.elementRow,backgroundColor:Colours.levelZeroBg,gap:5}}>
                <FontAwesomeIcon icon={props.icon} style={{color:Colours.levelFourBg}}></FontAwesomeIcon>
                <div style={{
                    ...InterfaceStyles.buttonText,
                    whiteSpace:'nowrap',
                    overflow:'hidden',
                }}>{props.text}</div>
                {props.children}
            </div>
        </div>
    );
};

export const InterfaceHorizontalSlider = (props:{
    icon?:IconDefinition,
    value:number,
    minValue:number,
    maxValue:number,
    decimalLength?:number,
    increment?:number,
    suffix?:string,
    inputWidth?:number,
    continualUpdate?:boolean,
    onChangeValue?:(value:number)=>void
}) => {

    //const Keyboard = useKeyboard();

    const [value, setValue] = useState(0);
    const [textValue, setTextValue] = useState(0);

    useEffect(()=>{
        setValue(props.value);
        setTextValue(props.value);
    },[props.value]);

    const [barWidth, setBarWidth] = useState(50);

    useEffect(()=>{
        const val = ((value - props.minValue) / (props.maxValue - props.minValue)) * 100;
        setBarWidth(val);
    },[value, props.minValue, props.maxValue]);

    const [dragging, setDragging] = useState(false);
    const barContainerRef = useRef<HTMLDivElement>(null);
    const barContainerRect = useRef<DOMRect | undefined>(null);

    const barDrag = (e:React.MouseEvent<HTMLDivElement,MouseEvent>) => {    
        if(barContainerRect.current){
            let percentage = +(((e.pageX - barContainerRect.current.left + 2) / (barContainerRect.current.right - barContainerRect.current.left)));
            let newValue = +(((props.maxValue - props.minValue) * percentage) + props.minValue).toFixed(props.decimalLength ?? 0);
            props.increment && props.increment != 0 && (newValue = Math.round(newValue / props.increment) * props.increment)
            newValue > props.maxValue && (newValue = props.maxValue);
            newValue < props.minValue && (newValue = props.minValue);
            setValue(newValue);
            setTextValue(newValue);
            props.continualUpdate && props.onChangeValue?.(newValue)
        }
    };


    return(
        <div style={{
            ...InterfaceStyles.standardRow,
            height:'100%',
            flex:1,
            gap:0,
            boxSizing:'border-box',
            border: `1px solid ${dragging ? Colours.orangeHighlightBg : Colours.levelThreeBg}`,
            borderRadius:5,
            overflow:'hidden',
        }}>
            {props.icon && (
                <div style={{...InterfaceStyles.iconContainer, borderRight:`1px solid ${Colours.levelThreeBg}`}}>
                    <FontAwesomeIcon icon={props.icon} style={{...InterfaceStyles.buttonText,color:Colours.orangeHighlightBg}}></FontAwesomeIcon>
                </div>
            )}
            <div
                ref={barContainerRef}
                style={{...InterfaceStyles.flexOne,height:'100%'}}
                onMouseDown={(e)=>{
                    barContainerRect.current = barContainerRef.current?.getBoundingClientRect();
                    setDragging(true);
                    barDrag(e);
                }}
                onMouseUp={(e)=>{
                    if(dragging){
                        barDrag(e);
                        props.continualUpdate !== true &&  props.onChangeValue?.(value);
                    }
                    setDragging(false);
                }}
                onMouseLeave={(e)=>{
                    if(dragging){
                        barDrag(e);
                        props.continualUpdate !== true &&  props.onChangeValue?.(value);
                    }
                    setDragging(false);
                }}
                onMouseMove={(e)=> dragging && barDrag(e)}
            >
                <div
                    style={{
                        display:'flex',
                        height:'100%',
                        width: `${barWidth}%`,
                        backgroundColor:dragging ? Colours.orangeHighlightBg : Colours.levelThreeBg,
                    }}    
                >
                </div>
            </div>
            <input 
                type="numeric"
                value={textValue} 
                style={{
                    ...InterfaceStyles.input,
                    flex:'none',
                    width: props.inputWidth ?? 50,
                    border:'none',
                    borderRadius:0,
                    borderLeft: `1px solid ${Colours.levelThreeBg}`,
                    paddingRight:1,
                }}
                onChange={(e)=>{
                    let numeric = +e.target.value
                    if(!Number.isNaN(numeric)){
                        setTextValue(+numeric.toFixed(props.decimalLength));
                    }
                }}
                onKeyDown={(e)=>{
                    if(e.key === 'Enter'){
                        let returnValue = textValue;
                        returnValue > props.maxValue && (returnValue = props.maxValue);
                        returnValue < props.minValue && (returnValue = props.minValue);
                        props.increment && props.increment != 0 && (returnValue = Math.round(returnValue / props.increment) * props.increment)
                        props.onChangeValue?.(returnValue);
                    }
                }}
                onFocus={()=>{
                    //Keyboard.setBypass?.(true);
                }}
                onBlur={()=>{
                    //Keyboard.setBypass?.(false);
                }}
            ></input>
            <div style={{...InterfaceStyles.buttonText,paddingRight:10,}}>{props.suffix}</div>
        </div>
    );

};

export const InterfaceAccordion = (props:{values:string[],value:string,onSelect:(value:string)=>void}) => {

    const [open, setOpen] = useState(false);

    const [value, setValue] = useState(props.value);

    useEffect(()=>{
        setValue(props.value);
    },[props.value]);

    return(
        <div style={{
            ...InterfaceStyles.floatingSection,
            gap:0,
            maxHeight: open ? (props.values.length) * 40 : 40,
            transition: 'max-height 0.5s',
        }}>
            <div style={{...InterfaceStyles.standardRow}}>
                <InterfaceButton icon={faChevronDown} text={value.charAt(0).toUpperCase() + value.slice(1)} style={{border:'none',flex:1,justifyContent:'flex-start'}} onClick={()=>{
                    setOpen(!open);
                }}></InterfaceButton>
            </div>
            {props.values.filter(val => val !== value !).map((val) => (
                <div key={`${v4()}`} style={InterfaceStyles.standardRow}>
                    <InterfaceButton text={val.charAt(0).toUpperCase() + val.slice(1)} style={{flex:1,borderRadius:0,borderWidth:0,borderTopWidth:1,justifyContent:'flex-start'}} onClick={()=>{
                        setOpen(false);
                        props.onSelect(val);
                    }}></InterfaceButton>
                </div>
            ))}
        </div>
    );

};

export const InterfaceColourInput = (props:{
    value:string,
    style?:CSSProperties,
    onChange?:(value:string) => void
}) => {

    const [displayValue, setDisplayValue] = useState(props.value);

    useEffect(()=>{
        setDisplayValue(props.value);
    },[props.value]);

    return(
        <div style={{
            ...InterfaceStyles.standardRow,
            border: `1px solid ${Colours.levelThreeBg}`,
            flex:1,
            borderRadius:5,
            overflow:'hidden',
        }}>
            <input 
                type="color" 
                value={displayValue} 
                style={{
                    flex:1,
                    outline:'none',
                    border:'none',
                    margin:-10,
                    height:60,
                    backgroundColor:Colours.levelOneBg,
                }}
                onChange={(evt)=>{
                    const hexValue = evt.target.value;
                    setDisplayValue(hexValue);
                    props.onChange?.(hexValue);
                }}
            ></input>
        </div>
    );

};